<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButtonBack href="/report" :query="{ domain: domain }" />
            <b-dropdown id="unduh-dropdown" right variant="primary" menu-class="dropdown-menu-end">
              <template v-slot:button-content>
                <i class="mc-fa icon-outline_download"></i>{{ $t('global.action.download') }}
              </template>
              <b-dropdown-item href="#" @click="downloadReport('xlsx')">
                <img :src="excelIcon" alt="excel" />
                {{ $t('global.action.downloadExcel') }}
              </b-dropdown-item>
              <b-dropdown-item href="#" @click="downloadReport('pdf')">
                <img :src="pdfIcon" alt="pdf" />
                {{ $t('global.action.downloadPdf') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </PageHeader>
    </div>
    <div class="d-flex align-items-center gap-2 me-2 absolute-right">
      <FavoriteReportButton
        v-if="hasAccess(['favorite-reports:update']) && !report.isSaved"
        :report="report"
      />
      <ModalSavedReport
        v-if="hasAccess(['saved-reports:read']) && !report.isSaved"
        :filter="filterValue"
        :detail="filterDetail"
        :report="report"
      />
      <McButton
        variant="secondary"
        :text="$t('report.title.reportParameter')"
        icon="icon-outline_filter"
        @click="showModal"
      />
    </div>
    <b-card :key="summaryKey" class="mb-2 mc-general__label" style="margin-top: 80px">
      <h5 class="mc-general__label">{{ $t('report.fields.dataSummary') }}</h5>
      <b-row>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="licensePlate_label"
            :label="$t('report.fields.licensePlate')"
            label-for="licensePlate"
          >
            <div v-if="!isBusy" class="d-flex align-items-center gap-2">
              <span>{{ licensePlateLabel }}</span>
              <McBadge
                v-if="vehicleIdCount > 2"
                :text="`+${vehicleIdCount - 2}`"
                variant="success"
                size="small"
                type="pill"
              />
            </div>
            <b-skeleton width="70%" v-else />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <McGrapqlTable
      :key="tableKey"
      bordered
      withCard
      searchable
      :fields="fields"
      :apiClient="apiClient"
      :hasCheckbox="false"
      :lazyLoadAddress="columns.includes('address')"
      reportKey="vehicle-last-location"
    >
      <template #viewManager="{ handleSetDataFields, dataColumns, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataColumns"
          @setColumn="handleSetDataFields"
          @setColumnReport="handleColumnReport"
        />
      </template>
      <template #cell(lastPositionStatus)="data">
        {{ momentDateShortMonth(data.item.lastPositionStatus) }}
        <span class="text-muted">{{ momentTimeSeconds(data.item.lastPositionStatus, true) }}</span>
      </template>
      <template #cell(lastReceive)="data">
        {{ momentDateShortMonth(data.item.lastReceive) }}
        <span class="text-muted">{{ momentTimeSeconds(data.item.lastReceive, true) }}</span>
      </template>
      <template #cell(motionStatus)="data">
        {{ generateStatus(data.item.motionStatus) }}
      </template>
      <template #cell(acc)="data">
        {{ data.item.acc == 'on' ? $t('report.label.on') : $t('report.label.off') }}
      </template>
      <template #cell(fuel)="data">
        {{ data.item.fuel !== null ? minimizeFloat(data.item.fuel) : '-' }}
      </template>
      <template #cell(speed)="data">
        {{ data.item.speed !== null ? minimizeFloat(data.item.speed) : '-' }}
      </template>
      <template #cell(temperature)="data">
        {{ data.item.temperature !== null ? minimizeFloat(data.item.temperature) : '-' }}
      </template>
      <template #cell(temperature2)="data">
        {{ data.item.temperature2 !== null ? minimizeFloat(data.item.temperature2) : '-' }}
      </template>
      <template #cell(temperature3)="data">
        {{ data.item.temperature3 !== null ? minimizeFloat(data.item.temperature3) : '-' }}
      </template>
      <template #cell(refrigerator)="data">
        {{
          data.item.refrigerator
            ? data.item.refrigerator == 'on'
              ? $t('report.label.on')
              : $t('report.label.off')
            : '-'
        }}
      </template>

      <template #cell(door1)="data">
        <span v-if="data.item.door1 !== null">{{
          data.item.door1 == 1 ? $t('global.alert.close') : $t('global.alert.open')
        }}</span>
        <span v-else>-</span>
      </template>

      <template #cell(door2)="data">
        <span v-if="data.item.door2 !== null">{{
          data.item.door2 == 1 ? $t('global.alert.close') : $t('global.alert.open')
        }}</span>
        <span v-else>-</span>
      </template>
      <template #cell(address)="data">
        <b-skeleton v-if="data.item.address == ''" width="70%" />
        <AddressValue
          v-else
          :id="`address-${data.index}`"
          :latitude="data.item.latitude"
          :longitude="data.item.longitude"
          :address="data.item.address"
        />
      </template>
      <template #cell(vgroups)="data">
        <HuggedValue
          :id="`vgroups-${data.index}`"
          :value="data.item.vgroups ? data.item.vgroups.map((item) => item.name).join(', ') : null"
        />
      </template>
    </McGrapqlTable>
    <ModalFilter
      v-if="isMounted"
      ref="modal-filter"
      :report="report"
      :unRedirect="true"
      @refresh-table="forceRerender"
    />
    <McReportDownloader />
    <ModalSavedReportVerification
      :key="modalVerifReport"
      :domain="domain"
      :message="message"
      :error="isError"
    />
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import McFilterSelect from '@/components/McFilterSelect'
import McReportDownloader from '@/components/McReportDownloader'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import fields from './lib/fields/fields'
import { VEHICLE_STATUS_REPORT, EXPORT_VEHICLE_STATUS_REPORT } from '@/services/api/report/report'
import reportList from '../../lib/reportList.js'
import { MixPanel } from '@/mixin/mixpanel'
import ModalFilter from '@/router/views/report/components/ModalFilter'
import ModalSavedReportVerification from '@/router/views/report/components/ModalSavedReportVerification'
import McButton from '@/components/McButton'
import ModalSavedReport from '@/router/views/report/components/ModalSavedReport'
import { QueryCache } from '@/mixin/query-cache'
import responsive from '@/mixin/responsive'
import FavoriteReportButton from '@/router/views/report/components/FavoriteReportButton'
import { mapGetters } from 'vuex'
import McBadge from '@/components/McBadge'
import AddressValue from '../../components/AddressValue.vue'
import HuggedValue from '../../components/HuggedValue.vue'

moment.locale('id')

export default {
  page: {
    title: 'Report',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McGrapqlTable,
    McFilterSelect,
    McReportDownloader,
    ModalSavedReportVerification,
    FavoriteReportButton,
    ModalSavedReport,
    McBadge,
    ModalFilter,
    McButton,
    AddressValue,
    HuggedValue,
  },
  mixins: [Helper, MixPanel, responsive],
  data() {
    return {
      isMounted: false,
      excelIcon: require('@/assets/images/download-format/excel.png'),
      pdfIcon: require('@/assets/images/download-format/pdf.png'),
      filters: {},
      tableKey: 0,
      summary: {},
      isBusy: false,
      reportFields: fields.map((item, i) => ({ ...item, order: i + 1 })),
      reportKey: 'vehicle-last-location',
      filterDetail: {},
      summaryKey: 50,
      queryCache: new QueryCache(),
      message: '',
      isError: false,
      modalVerifReport: 100,
    }
  },
  mounted() {
    this.reportKey =
      this.$route.query.key && this.savedReport.find((item) => item.key === this.$route.query.key)
        ? this.$route.query.key
        : this.reportKey
    const { reportFilter, reportFilterValue } = this.getFilterReport(this.reportKey)
    this.$store.commit('report/updateFilter', reportFilter)
    this.$store.commit('report/updateFilterValue', reportFilterValue)
    this.filters = { ...this.$store.state.report.filterReport }
    this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
    this.isMounted = true
    this.track('View ' + this.report.event)
    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    domain() {
      return this.report.domain
    },
    title() {
      return this.report.labelName ?? this.$t(this.report.label)
    },
    fields() {
      return this.reportFields
    },
    columns() {
      return this.reportFields.map((field) => field.header)
    },
    generateFilter() {
      const { valid, msg } = this.validateFilter(this.report.filter, this.filters)
      if (valid) {
        const filters = {}
        Object.keys(this.filters).forEach((item) => {
          if (
            this.filters[item] !== null &&
            ((Array.isArray(this.filters[item]) && this.filters[item].length > 0) ||
              (!Array.isArray(this.filters[item]) && this.filters[item]))
          ) {
            filters[item] = this.filters[item]
          }
        })
        return { valid: true, model: filters }
      } else {
        this.$mcToast.error(msg)
        return { valid: false, model: {} }
      }
    },
    apiClient() {
      return {
        get: async (filter) => {
          const { valid, model } = this.generateFilter
          if (!valid) return {}
          const self = this
          self.isBusy = true
          const res = await this.queryCache.fetch(
            VEHICLE_STATUS_REPORT,
            {
              filter: {
                ...filter,
                ...model,
                column: this.columns.filter((item) => item != 'address'),
              },
            },
            self.reportKey
          )
          self.isBusy = false
          self.message = res.lastLocationVehicle.message
          self.isError = !res.lastLocationVehicle.isSuccessful
          self.modalVerifReport += 1
          const result = res.lastLocationVehicle.lastLocationVehicle
          result.data.forEach((item) => {
            item.address = ''
          })
          return result
        },
      }
    },
    licensePlateLabel() {
      if (this.filters.vehicleId && this.filters.vehicleId.length > 0) {
        return this.filterDetail != {} ? this.filterDetail.licensePlate : '-'
      } else {
        return '-'
      }
    },
    vehicleIdCount() {
      return this.filters.vehicleId && this.filters.vehicleId.length > 0
        ? this.filters.vehicleId.length
        : 0
    },
    savedReport() {
      return JSON.parse(localStorage.getItem('saveReports'))
    },
    report() {
      const reports = [...reportList].concat(this.savedReport)
      return reports.find((item) => item.key == this.reportKey)
    },
    filterValue() {
      return this.$store.state.report.filterValue
    },
  },
  methods: {
    handleColumnReport(fields) {
      this.reportFields = fields
    },
    forceRerender() {
      this.filters = { ...this.$store.state.report.filterReport }
      this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
      this.tableKey += 1
      this.summaryKey += 1
    },
    showModal() {
      this.$refs['modal-filter'].SetFormValues(this.filterValue)
      this.$bvModal.show(this.report.modal)
    },
    redirToMap(lat, long) {
      let url = `https://www.google.com/maps/search/?api=1&query=${lat},${long}`
      window.open(url, '_blank')
    },
    formatTimePart(value) {
      return value < 10 ? `0${value}` : value
    },
    formatTime(hours, minutes, seconds) {
      return `${this.formatTimePart(hours)}:${this.formatTimePart(minutes)}:${this.formatTimePart(
        seconds
      )}`
    },
    generateStatus(status) {
      switch (status) {
        case 'I':
          return this.$t('global.filter.idle')
        case 'M':
          return this.$t('global.filter.running')
        case 'A':
          return this.$t('global.filter.assisted')
        case 'O':
          return this.$t('global.filter.offline')
        case 'P':
          return this.$t('global.filter.parking')
        default:
          return this.$t('global.filter.unknown')
      }
    },
    showDownloader(date, extension, result) {
      const downloadProgress = {
        status: 'I',
        url: null,
        name: `${this.title} ${date}.${extension}`,
        periode: date,
        downloadedAt: moment().format('DD MMM YYYY HH:mm:ss'),
        type: extension,
        id: result.lastLocationVehicleReport.id,
      }
      if (result.isSuccessful) {
        this.$store.commit('report/doneProgress', downloadProgress)
      } else {
        downloadProgress.status = 'F'
        this.$store.commit('report/failProgress', downloadProgress)
      }
      this.$store.commit('report/showDownloadPopup', true)
    },
    downloadReport(extension) {
      this.track(`Download ${this.report.event} ${extension == 'pdf' ? '- PDF' : '- Excel'}`)

      const { valid, model } = this.generateFilter
      if (!valid) {
        setTimeout(() => {
          this.showModal()
        }, 1000)
        return false
      }

      const date = moment().format('DD MMMM YYYY HH:mm:ss')
      let filterVar = {
        ...model,
        column: this.fields.map((field) => field.key),
        subtitle: `Data diambil pada : ${date}`,
        downloadAs: extension == 'pdf' ? 'pdf' : 'excel',
      }
      if (this.report.labelName) {
        filterVar.title = this.report.labelName
      }

      this.$apollo
        .query({
          query: EXPORT_VEHICLE_STATUS_REPORT,
          variables: {
            filter: filterVar,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          this.showDownloader(date, extension, res.data.lastLocationVehicleReport)
        })
    },
  },
}
</script>
